import { Box, Button, List, Typography } from '@mui/material';
import { useSnackbar } from 'notistack';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import { FileItem } from '@/common';
import { downloadFileFromResponse, getMessageFromError } from '@/core';
import { downloadFileFromAuditorVisitRapReview } from '../../api/downloadFileFromAuditorVisitRapReview';
import { AuditVisitRepReviewDetail } from '../../types';
import FileUploadModal from '../../components/FileUploadModal';
import { updateAuditorVisitRepReview } from '../../api/updateAuditorVisitRepReview';

type FilesProps = {
  auditorVisitRepReview: AuditVisitRepReviewDetail;
};

const Files = ({ auditorVisitRepReview }: FilesProps) => {
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const [showFileUploadModal, setShowFileUploadModal] = useState(false);
  const [sqoFileId, setSqoFileId] = useState<string | undefined>('');

  const { mutate } = useMutation({
    mutationFn: updateAuditorVisitRepReview,
    onSuccess: () => {
      queryClient.invalidateQueries(['audit-visit-rep-review-id', auditorVisitRepReview.id]);
    },
    onError: (error: Error) => {
      enqueueSnackbar(getMessageFromError(error), { variant: 'error' });
    },
  });

  const downloadFileMutation = useMutation({
    mutationFn: downloadFileFromAuditorVisitRapReview,
    onSuccess: async (response) => {
      await downloadFileFromResponse(response);
      if (!auditorVisitRepReview.datWork) {
        mutate({
          id: auditorVisitRepReview.id,
          data: {
            ...auditorVisitRepReview,
            status: { id: 'EB5489CD-C1F4-4659-87C5-1746978513F0' },
          },
        });
      }
      enqueueSnackbar(
        t('Successfully downloaded file.', { defaultValue: 'Successfully downloaded file.' }),
        { variant: 'success' }
      );
    },
    onError: (error: Error) => {
      enqueueSnackbar(getMessageFromError(error), { variant: 'error' });
    },
  });

  const handleDownload = (id: string, fileId: string) => {
    downloadFileMutation.mutate({ id, fileId });
  };

  return (
    <Box>
      <FileUploadModal
        sqsfileId={sqoFileId}
        show={showFileUploadModal}
        setShow={setShowFileUploadModal}
        visitRepReviewId={auditorVisitRepReview.id}
      />
      {auditorVisitRepReview.documents.length > 0 ? (
        <List>
          {auditorVisitRepReview.documents.map((item) => (
            <FileItem
              key={item.id}
              name={item.title || ''}
              uploadedBy={item.uploadedBy}
              uploadedAt={item.uploadedAt}
              version={item.version}
              fileName={item.fileName}
              onDownload={
                () => {
                  handleDownload(auditorVisitRepReview.id, item.id);
                }
                // downloadFileMutation.mutate({ id: auditorVisitRepReview.id, fileId: item.id })
              }
              onNewVersion={() => {
                setSqoFileId(item.sqsfileId);
                setShowFileUploadModal(true);
              }}
            />
          ))}
        </List>
      ) : (
        <Typography variant="subtitle1">{t('No files', { defaultValue: 'No files' })}</Typography>
      )}
    </Box>
  );
};

export default Files;
